/**
 * Map marker creation utilities for Google Maps
 * Based on map-marker-fns.ts and vehicles.ts from main application
 */

// Cache for marker library to avoid redundant imports
let markerLibraryPromise: Promise<google.maps.MarkerLibrary> | null = null;

const MARKER_CONFIG = {
  Z_INDEX: {
    SHADOW: 0,
    TRAIL: 1,
    BREADCRUMB: 5,
    MAIN_MARKER: 10,
  },
  TRAIL: {
    SHADOW_COLOR: '#000000',
    SHADOW_OPACITY: 0.3,
    SHADOW_WEIGHT: 5,
    MAIN_OPACITY: 1.0,
    MAIN_WEIGHT: 3,
  },
} as const;

export interface MarkerOptions {
  position: google.maps.LatLngLiteral;
  heading: number;
  color: string;
  isMain?: boolean;
}

/**
 * Create compass marker HTML content for main aircraft
 * Based on displayMarker from map-marker-fns.ts:37-112
 * Main marker uses PNG background, breadcrumbs use CSS triangle
 */
export const createCompassMarkerElement = (options: MarkerOptions): HTMLDivElement => {
  const { heading, color, isMain = false } = options;

  const container = document.createElement('div');
  container.className = isMain ? 'marker-container' : 'breadcrumb-container';

  const markerDiv = document.createElement('div');
  markerDiv.className = isMain ? 'compass-marker' : 'breadcrumb-marker';
  markerDiv.style.backgroundColor = color;
  // Rotation for heading - PNG compass will rotate for main, triangle for breadcrumb
  markerDiv.style.transform = `rotate(${heading}deg)`;

  // Add triangle indicator for breadcrumbs to show heading
  if (!isMain) {
    const triangle = document.createElement('div');
    triangle.className = 'breadcrumb-triangle';
    markerDiv.appendChild(triangle);
  }

  container.appendChild(markerDiv);

  return container;
};

/**
 * Create or update main aircraft marker
 * Based on vehicles.ts:175-216 displayMarker
 */
export const createMainMarker = async (
  map: google.maps.Map,
  options: MarkerOptions
): Promise<google.maps.marker.AdvancedMarkerElement> => {
  // Import marker library only once and cache the promise
  if (!markerLibraryPromise) {
    markerLibraryPromise = google.maps.importLibrary("marker") as Promise<google.maps.MarkerLibrary>;
  }
  await markerLibraryPromise;

  const content = createCompassMarkerElement({ ...options, isMain: true });

  const advancedMarker = new google.maps.marker.AdvancedMarkerElement({
    map,
    position: options.position,
    content,
    zIndex: MARKER_CONFIG.Z_INDEX.MAIN_MARKER,
  });

  return advancedMarker;
};

/**
 * Create breadcrumb marker for historical positions with compass arrow
 * Based on displayBreadcrumb from map-marker-fns.ts:114-133
 * Uses AdvancedMarkerElement with custom HTML content
 */
export const createBreadcrumbMarker = async (
  map: google.maps.Map,
  options: MarkerOptions
): Promise<google.maps.marker.AdvancedMarkerElement> => {
  // Import marker library (shared cache with main marker)
  if (!markerLibraryPromise) {
    markerLibraryPromise = google.maps.importLibrary("marker") as Promise<google.maps.MarkerLibrary>;
  }
  await markerLibraryPromise;

  const content = createCompassMarkerElement({ ...options, isMain: false });

  const marker = new google.maps.marker.AdvancedMarkerElement({
    map,
    position: options.position,
    content,
    zIndex: MARKER_CONFIG.Z_INDEX.BREADCRUMB,
  });

  return marker;
};

/**
 * Create trail polyline connecting positions with shadow effect
 * Based on vehicles.ts:351-360 displayTrail
 * Trail has low zIndex so breadcrumbs appear on top
 * Returns an array: [shadowPolyline, mainPolyline]
 */
export const createTrailPolyline = (
  map: google.maps.Map,
  positions: google.maps.LatLngLiteral[],
  color: string
): google.maps.Polyline[] => {
  // Create shadow polyline (darker, wider, underneath)
  const shadowPolyline = new google.maps.Polyline({
    path: positions,
    geodesic: true,
    strokeColor: MARKER_CONFIG.TRAIL.SHADOW_COLOR,
    strokeOpacity: MARKER_CONFIG.TRAIL.SHADOW_OPACITY,
    strokeWeight: MARKER_CONFIG.TRAIL.SHADOW_WEIGHT,
    zIndex: MARKER_CONFIG.Z_INDEX.SHADOW,
    map: map,
  });

  // Create main trail polyline (fully opaque so shadow doesn't show through)
  const mainPolyline = new google.maps.Polyline({
    path: positions,
    geodesic: true,
    strokeColor: color,
    strokeOpacity: MARKER_CONFIG.TRAIL.MAIN_OPACITY,
    strokeWeight: MARKER_CONFIG.TRAIL.MAIN_WEIGHT,
    zIndex: MARKER_CONFIG.Z_INDEX.TRAIL,
    map: map,
  });

  return [shadowPolyline, mainPolyline];
};

/**
 * Update marker position and rotation
 */
export const updateMarkerPosition = (
  marker: google.maps.marker.AdvancedMarkerElement,
  position: google.maps.LatLngLiteral,
  heading: number,
  color: string
): void => {
  marker.position = position;

  // Update content (rotation and color)
  const content = marker.content as HTMLElement;
  if (content) {
    const markerDiv = content.querySelector('.compass-marker, .breadcrumb-marker') as HTMLElement;
    if (markerDiv) {
      markerDiv.style.transform = `rotate(${heading}deg)`;
      markerDiv.style.backgroundColor = color;
    }
  }
};
